<template>
  <section class="py-12">
    <div class="flex justify-center">
      <div class="w-full px-3 desktop:max-w-[1000px] desktop:px-0">
        <h2 class="text-h2 mb-1">{{ t('title') }}</h2>
        <p class="mb-6 text-sm text-gray-600">
          <span>
            {{ $t('form.fill-prompt') }}
          </span>
          <span class="ml-1 text-red-400">* {{ $t('form.required') }}</span>
        </p>
        <div :class="isSubmitting && 'pointer-events-none opacity-60'">
          <div class="tablet:flex tablet:gap-4">
            <div class="mb-5 tablet:w-1/2">
              <FieldInputText
                id="form_first-name"
                v-model="form.firstName"
                :name="$t('form.first-name')"
                :is-error="!!formErrorMessage.firstName"
                :error-message="formErrorMessage.firstName"
                required
                @blur="solutionsStore.VALID_FORM_FIRST_NAME"
                @change="solutionsStore.VALID_FORM_FIRST_NAME"
              />
            </div>
            <div class="mb-5 tablet:w-1/2">
              <FieldInputText
                id="form_last-name"
                v-model="form.lastName"
                :name="$t('form.last-name')"
                :is-error="!!formErrorMessage.lastName"
                :error-message="formErrorMessage.lastName"
                required
                @blur="solutionsStore.VALID_FORM_LAST_NAME"
                @change="solutionsStore.VALID_FORM_LAST_NAME"
              />
            </div>
          </div>
          <div class="tablet:flex tablet:gap-4">
            <div class="mb-5 tablet:w-1/2">
              <FieldInputText
                id="form_email"
                v-model="form.email"
                :name="$t('form.email')"
                :is-error="!!formErrorMessage.email"
                :error-message="formErrorMessage.email"
                type="email"
                placeholder="example@example.com"
                required
                @blur="solutionsStore.VALID_FORM_EMAIL"
                @change="solutionsStore.VALID_FORM_EMAIL"
              />
            </div>
            <div class="mb-5 tablet:w-1/2">
              <FieldInputPhone
                id="form_phone"
                v-model:phone-code="form.phoneCode"
                v-model:phone-number="form.phoneNumber"
                :name="$t('form.phone-number')"
                :is-error="
                  !!formErrorMessage.phoneCode || !!formErrorMessage.phoneNumber
                "
                :error-message="
                  formErrorMessage.phoneCode || formErrorMessage.phoneNumber
                "
                :placeholder="$t('form.phone-number-placeholder')"
                required
                @code-blur="solutionsStore.VALID_FORM_PHONE_CODE"
                @code-change="solutionsStore.VALID_FORM_PHONE_CODE"
                @number-blur="solutionsStore.VALID_FORM_PHONE_NUMBER"
                @number-change="solutionsStore.VALID_FORM_PHONE_NUMBER"
              />
            </div>
          </div>
          <div class="tablet:flex tablet:gap-4">
            <div class="mb-5 tablet:w-1/2">
              <FieldInputText
                id="form_company-name"
                v-model="form.companyName"
                :name="$t('form.company-name')"
                :is-error="!!formErrorMessage.companyName"
                :error-message="formErrorMessage.companyName"
                required
                @blur="solutionsStore.VALID_FORM_COMPANY_NAME"
                @change="solutionsStore.VALID_FORM_COMPANY_NAME"
              />
            </div>
            <div class="mb-5 tablet:w-1/2">
              <FieldSelect
                id="form_country"
                v-model="form.country"
                :name="$t('form.country-region')"
                :is-error="!!formErrorMessage.country"
                :error-message="formErrorMessage.country"
                required
                @change="solutionsStore.VALID_FORM_COUNTRY"
              >
                <option value="" disabled>
                  {{ $t('form.select') }}
                </option>
                <option v-for="item in countries" :key="item" :value="item">
                  {{ item }}
                </option>
              </FieldSelect>
            </div>
          </div>
          <div class="mb-5 tablet:w-1/2 tablet:pr-2">
            <FieldSelect
              id="form_employees"
              v-model="form.employeeRange"
              :name="$t('form.employees')"
              :is-error="!!formErrorMessage.employeeRange"
              :error-message="formErrorMessage.employeeRange"
              required
              @change="solutionsStore.VALID_FORM_EMPLOYEES_RANGE"
            >
              <option :value="0" disabled>
                {{ $t('form.select') }}
              </option>
              <option
                v-for="item in employeeRanges"
                :key="item.id"
                :value="item.id"
              >
                {{ item.description }}
              </option>
            </FieldSelect>
          </div>

          <FormPrivacy
            :props-privacy="privacy"
            :country-name="form.country"
            @update="handleUpdatePrivacy"
            @valid="handleValidPrivacy"
          />

          <FormCaptcha
            class="mb-5"
            @on-verify="verifyCaptcha"
            @on-expire="expireCaptcha"
          />

          <div class="flex flex-col gap-3 tablet:flex-row tablet:gap-4">
            <TheButton
              class="flex w-full items-center justify-center tablet:w-1/2"
              :title="$t('form.submit')"
              @click="handleSubmit"
            >
              <span class="text-sm leading-6">
                {{ $t('form.submit') }}
              </span>
              <IconLoader v-if="isSubmitting" class="ml-1 h-4 w-4 text-white" />
            </TheButton>
            <TheButton
              type="outline"
              :to="`/${locale}/marketplace/solutions/${solutionAlias}`"
              :title="$t('form.back')"
              class="w-full tablet:w-1/2"
            >
              <span class="text-sm">
                {{ $t('form.back') }}
              </span>
            </TheButton>
          </div>

          <p class="mt-3 text-center text-sm text-gray-600">
            {{ $t('form.privacy-policy.first-part') }}
            <TheNuxtLink
              :to="privacyPolicyUrl"
              :title="$t('form.privacy-policy.policy')"
              class="text-blue-400 hover:text-blue-500 hover:underline"
            >
              {{ $t('form.privacy-policy.policy') }}
            </TheNuxtLink>
            {{ $t('form.privacy-policy.second-part') }}
          </p>
          <p v-if="!isCN" class="text-center text-sm text-gray-600">
            {{ $t('form.privacy-policy.recaptcha-first-part') }}
            <TheNuxtLink
              class="text-blue-400 hover:text-blue-500 hover:underline"
              :title="$t('form.privacy-policy.policy')"
              to="https://policies.google.com/privacy"
            >
              {{ $t('form.privacy-policy.policy') }}
            </TheNuxtLink>
            {{ $t('form.privacy-policy.recaptcha-second-part') }}
            <TheNuxtLink
              class="text-blue-400 hover:text-blue-500 hover:underline"
              :title="$t('form.privacy-policy.service')"
              to="https://policies.google.com/terms"
            >
              {{ $t('form.privacy-policy.service') }}
            </TheNuxtLink>
            {{ $t('form.privacy-policy.recaptcha-third-part') }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { IFormPrivacy } from '@/components/FormPrivacy.vue';
  import { EnumSolutionsLeadType } from '@/stores/solutions/solutionsStore';

  const { query } = useRoute();
  const route = useRoute();
  const { t, locale } = useI18n({ useScope: 'local' });
  const { privacyPolicyUrl } = useWebsiteUrl();
  const { isCN } = useLanguage();
  const {
    captcha,
    verifyCaptcha,
    expireCaptcha,
    validateCaptcha,
    resetCaptcha,
  } = useCaptcha('solution_book_a_demo');

  const mainStore = useMainStore();
  const profileStore = useProfileStore();
  const solutionsStore = useSolutionsStore();
  const { countries, employeeRanges, isShowFormWarningPopUp } =
    storeToRefs(mainStore);
  const { profile } = storeToRefs(profileStore);
  const { form, formErrorMessage, isSubmitting, isShowResult } =
    storeToRefs(solutionsStore);

  const { solution, generic } = route.params;
  const solutionAlias = generic ? `${solution}/${generic}` : solution;

  const isValidPrivacy = ref<boolean>(true);

  const privacy = ref<IFormPrivacy>({
    japanAgreeSharePersonalData: false,
    turkeyAgreeSharePersonalData: false,
    koreaAgreeAdvPolicy: false,
    koreaAgreePersonalInfo: false,
  });

  const handleUpdatePrivacy = (payload: IFormPrivacy): void => {
    Object.assign(privacy.value, payload);
  };

  const handleValidPrivacy = (payload: boolean): void => {
    isValidPrivacy.value = payload;
  };

  const handleSubmit = async (): Promise<void> => {
    if (isSubmitting.value) {
      return;
    }

    const isValidateForm = solutionsStore.VALID_FORM() && isValidPrivacy.value;

    if (!isValidateForm) {
      isShowFormWarningPopUp.value = true;
      return;
    }

    const isValidateCaptcha = await validateCaptcha();
    if (!isValidateCaptcha) return;

    await postFormData();
  };

  const setUpFormData = (): FormData => {
    const formData = new FormData();

    formData.append('captcha.token', captcha.value.token);
    formData.append('firstName', form.value.firstName);
    formData.append('lastName', form.value.lastName);
    formData.append('emailAddress', form.value.email);
    formData.append('phoneCountryCode', form.value.phoneCode);
    formData.append('phoneNumber', form.value.phoneNumber);
    formData.append('companyName', form.value.companyName);
    formData.append('country', form.value.country);
    formData.append('employeeRange', String(form.value.employeeRange));

    // Append utm params
    const utmSource: string = (query?.utm_source as string) || '';
    const utmMedium: string = (query?.utm_medium as string) || '';

    formData.append('utmSource', utmSource);
    formData.append('utmMedium', utmMedium);

    formData.append('websiteUrl', window.location.href);

    return formData;
  };

  const postFormData = async (): Promise<void> => {
    isSubmitting.value = true;
    const formData = setUpFormData();
    try {
      await solutionsStore.POST_SOLUTIONS_LEADS(
        EnumSolutionsLeadType.BOOK_A_DEMO,
        formData
      );
      isShowResult.value = true;
    } catch (error) {
      console.log(error);
    } finally {
      resetCaptcha();
      isSubmitting.value = false;
    }
  };

  onBeforeMount(() => {
    solutionsStore.RESET_FORM();
    if (profile.value.isLogin) {
      solutionsStore.FILL_FORM_WITH_PROFILE();
    }
  });

  watch(
    () => profile.value.isLogin,
    (value: boolean) => {
      if (value) {
        solutionsStore.FILL_FORM_WITH_PROFILE();
      }
    }
  );
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Book a Demo"
  },
  "en-jp": {
    "title": "Book a Demo"
  },
  "en-tw": {
    "title": "Book a Demo"
  },
  "en-kr": {
    "title": "Book a Demo"
  },
  "en-americas": {
    "title": "Book a Demo"
  },
  "en-asia-pacific": {
    "title": "Book a Demo"
  },
  "en-emea": {
    "title": "Book a Demo"
  },
  "ja-jp": {
    "title": "デモのご予約はこちら"
  },
  "zh-cn": {
    "title": "预约产品演示"
  },
  "zh-tw": {
    "title": "預約產品展示"
  }
}
</i18n>
